import { closeModal } from "app/slices/modalSlice";

import { useTranslation } from "react-i18next";
import { Button, ControlledSelectGray } from "components/Shared";
import { useDispatch } from "react-redux";
import { RFIApproval, RFIDraft } from "interfaces/router";

import { toast } from "sonner";
import { Controller, useForm } from "react-hook-form";
import { useApproveRFIMutation } from "api/institutional/rfi_api";
import { Send } from "lucide-react";
import useErrorListener from "hooks/useErrorListener";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

export const ApproveRFIModal = ({ data }: { data: RFIApproval }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const [mutateRFI, { error, isLoading }] = useApproveRFIMutation();

  const handleClose = () => {
    dispatch(closeModal());
  };

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<RFIDraft>();

  useErrorListener({ error });

  const doSubmit = async (values: RFIDraft) => {
    if (values.approved === undefined) {
      return toast.error(t("Please select approval status"));
    }

    try {
      await mutateRFI({
        rfi_id: data.rfi_id,
        approval: values.approved,
        approval_details: values.approval_details?.trim(),
      }).unwrap();
      toast.success(t("RFI Approval Processed"));
      handleClose();
    } catch (e) {
      toast.error(t("RFI Approval Failed to Process"));
    }
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t("Approve RFI")}</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit(doSubmit)} className="space-y-2">
        <span className="text-sm text-gray-500">
          {t("Fill out the approval details of the RFI")}
        </span>
        <Controller
          name="approved"
          rules={{
            // Custom validation needed because `required: true` treats false as invalid
            validate: (value) => value !== undefined,
          }}
          control={control}
          render={(renderProps) => (
            <ControlledSelectGray
              placeholder={t("Approve RFI")}
              renderProps={renderProps}
              options={[
                { label: t("Approved"), value: true as any },
                { label: t("Rejected"), value: false as any },
              ]}
            />
          )}
        />
        <Controller
          name="approval_details"
          control={control}
          render={({ field }) => (
            <Textarea
              rows={4}
              placeholder={t("Approval Details (Optional)")}
              {...field}
            />
          )}
        />
        <Button
          type="submit"
          disabled={!isValid || isLoading}
          className="w-full"
        >
          <Send />
          {t("Send")}
        </Button>
      </form>
    </DialogContent>
  );
};

export default ApproveRFIModal;
